import {FC, useEffect, useState} from 'react'
import {useAuth} from '../../auth'
import {useProcurementPermissions} from '../core/permissionAccess'
import {
  createTermsConditionsTemplate,
  deleteTermsConditionsTemplate,
  getCompanyGroupAdmins,
  getTermsConditionsTemplates,
  updateTermsConditionsTemplate,
} from '../core/_requests'
import {
  CompanyGroupAdmin,
  ProcurementTermsConditionsTemplate,
  ProcurementTermsTemplateScopeType,
} from '../core/_models'
import {PROCUREMENT_PERMS} from '../core/permissions'
import {toast} from 'react-toastify'

type Props = {
  searchTerm: string
}

type FormState = {
  id?: number | null
  title: string
  content: string
  scopeType: ProcurementTermsTemplateScopeType
  companyGroupAdminId: number | null
  defaultTemplate: boolean
  active: boolean
}

const defaultFormState: FormState = {
  id: null,
  title: '',
  content: '',
  scopeType: 'COMPANY_GROUP',
  companyGroupAdminId: null,
  defaultTemplate: false,
  active: true,
}

const TermsConditionsLibraryPanel: FC<Props> = ({searchTerm}) => {
  const {currentUser} = useAuth()
  const {can, getScope} = useProcurementPermissions()
  const canCreate = can(PROCUREMENT_PERMS.CREATE_TERMS_CONDITIONS_LIBRARY)
  const canUpdate = can(PROCUREMENT_PERMS.UPDATE_TERMS_CONDITIONS_LIBRARY)
  const canDelete = can(PROCUREMENT_PERMS.DELETE_TERMS_CONDITIONS_LIBRARY)
  const readScope = getScope(PROCUREMENT_PERMS.READ_TERMS_CONDITIONS_LIBRARY)
  const scopeAll = readScope === 'ALL'
  const actorCompanyGroupId =
    Number(
      (currentUser as any)?.companyGroupAdminId ??
        (currentUser as any)?.companyGroupAdmin?.id ??
        (currentUser as any)?.companyAdmin?.cgaid?.id ??
        (currentUser as any)?.cgaid?.id ??
        (currentUser as any)?.cgaid ??
        0
    ) || null
  const [rows, setRows] = useState<ProcurementTermsConditionsTemplate[]>([])
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<FormState>({
    ...defaultFormState,
    scopeType: 'COMPANY_GROUP',
    companyGroupAdminId: actorCompanyGroupId,
  })
  const [companyGroups, setCompanyGroups] = useState<CompanyGroupAdmin[]>([])

  const loadRows = async () => {
    try {
      const data = await getTermsConditionsTemplates({search: searchTerm || undefined})
      setRows(data)
    } catch (error: any) {
      toast.error(error.message || 'Failed to load terms templates')
    }
  }

  useEffect(() => {
    loadRows()
  }, [searchTerm])

  useEffect(() => {
    if (!scopeAll) return
    getCompanyGroupAdmins()
      .then(setCompanyGroups)
      .catch(() => setCompanyGroups([]))
  }, [scopeAll])

  const openCreate = () => {
    setForm({
      ...defaultFormState,
      scopeType: 'COMPANY_GROUP',
      companyGroupAdminId: actorCompanyGroupId,
      active: true,
    })
    setShowForm(true)
  }

  const openEdit = (row: ProcurementTermsConditionsTemplate) => {
    setForm({
      id: row.id,
      title: row.title || '',
      content: row.content || '',
      scopeType: (row.scopeType || 'COMPANY_GROUP') as ProcurementTermsTemplateScopeType,
      companyGroupAdminId: row.companyGroupAdminId ?? null,
      defaultTemplate: !!row.defaultTemplate,
      active: row.active !== false,
    })
    setShowForm(true)
  }

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Template title is required')
      return
    }
    if (!form.content.trim()) {
      toast.error('Terms content is required')
      return
    }
    if (form.scopeType === 'COMPANY_GROUP' && !form.companyGroupAdminId && !actorCompanyGroupId) {
      toast.error('Company group is required')
      return
    }
    setSaving(true)
    try {
      const payload = {
        title: form.title.trim(),
        content: form.content,
        scopeType: form.scopeType,
        companyGroupAdminId: scopeAll ? form.companyGroupAdminId : actorCompanyGroupId,
        companyAdminId: null,
        defaultTemplate: form.defaultTemplate,
        active: form.active,
      }
      if (form.id) {
        await updateTermsConditionsTemplate(form.id, payload)
      } else {
        await createTermsConditionsTemplate(payload)
      }
      setShowForm(false)
      await loadRows()
      toast.success(`Terms template ${form.id ? 'updated' : 'created'}`)
    } catch (error: any) {
      toast.error(error.message || 'Failed to save template')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (row: ProcurementTermsConditionsTemplate) => {
    if (!window.confirm(`Delete or deactivate template "${row.title}"?`)) return
    try {
      await deleteTermsConditionsTemplate(row.id)
      await loadRows()
      toast.success('Template removed')
    } catch (error: any) {
      toast.error(error.message || 'Failed to remove template')
    }
  }

  const scopeOptions: ProcurementTermsTemplateScopeType[] = scopeAll
    ? ['GLOBAL', 'COMPANY_GROUP']
    : ['COMPANY_GROUP']

  return (
    <div className='h-100 d-flex flex-column gap-3'>
      <div className='d-flex justify-content-between align-items-start'>
        <div>
          <h5 className='mb-1 fw-bold text-dark'>Terms & Conditions Library</h5>
          <div className='text-muted small'>
            Reusable standard terms templates with scope-aware defaults.
          </div>
        </div>
        {canCreate && (
          <button className='btn btn_primary btn-sm' onClick={openCreate}>
            Add Template
          </button>
        )}
      </div>

      {showForm && (canCreate || canUpdate) && (
        <div className='card shadow-sm border'>
          <div className='card-body row g-3'>
            <div className='col-md-4'>
              <label className='form-label fw-semibold text-dark'>Template Title</label>
              <input
                className='form-control'
                value={form.title}
                onChange={(e) => setForm((prev) => ({...prev, title: e.target.value}))}
                placeholder='Standard Supply Terms'
              />
            </div>
            <div className='col-md-3'>
              <label className='form-label fw-semibold text-dark'>Scope</label>
              <select
                className='form-select'
                value={form.scopeType}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    scopeType: e.target.value as ProcurementTermsTemplateScopeType,
                  }))
                }
              >
                {scopeOptions.map((option) => (
                  <option key={option} value={option}>
                    {option.replace('_', ' ')}
                  </option>
                ))}
              </select>
            </div>
            {scopeAll && form.scopeType === 'COMPANY_GROUP' && (
              <div className='col-md-3'>
                <label className='form-label fw-semibold text-dark'>Company Group</label>
                <select
                  className='form-select'
                  value={form.companyGroupAdminId ?? ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      companyGroupAdminId: e.target.value ? Number(e.target.value) : null,
                    }))
                  }
                >
                  <option value=''>Select Company Group</option>
                  {companyGroups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className='col-12'>
              <label className='form-label fw-semibold text-dark'>Terms & Conditions Content</label>
              <textarea
                className='form-control'
                rows={12}
                value={form.content}
                onChange={(e) => setForm((prev) => ({...prev, content: e.target.value}))}
                style={{whiteSpace: 'pre-wrap'}}
              />
            </div>
            <div className='col-md-3'>
              <div className='form-check mt-2'>
                <input
                  id='terms-default-template'
                  className='form-check-input'
                  type='checkbox'
                  checked={form.defaultTemplate}
                  onChange={(e) =>
                    setForm((prev) => ({...prev, defaultTemplate: e.target.checked}))
                  }
                />
                <label className='form-check-label' htmlFor='terms-default-template'>
                  Default template
                </label>
              </div>
            </div>
            <div className='col-md-3'>
              <div className='form-check mt-2'>
                <input
                  id='terms-active-template'
                  className='form-check-input'
                  type='checkbox'
                  checked={form.active}
                  onChange={(e) => setForm((prev) => ({...prev, active: e.target.checked}))}
                />
                <label className='form-check-label' htmlFor='terms-active-template'>
                  Active
                </label>
              </div>
            </div>
            <div className='col-md-6 d-flex justify-content-end gap-2'>
              <button className='btn btn-light btn-sm' onClick={() => setShowForm(false)}>
                Cancel
              </button>
              <button className='btn btn_primary btn-sm' disabled={saving} onClick={handleSave}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className='card shadow-sm border h-100'>
        <div className='table-responsive' style={{maxHeight: '100%'}}>
          <table className='table table-striped table-bordered align-middle mb-0'>
            <thead className='table-light' style={{position: 'sticky', top: 0, zIndex: 1}}>
              <tr className='fw-semibold text-uppercase text-muted fs-8'>
                <th style={{minWidth: 160}}>Title</th>
                <th style={{minWidth: 120}}>Scope</th>
                <th style={{minWidth: 160}}>Company</th>
                <th style={{minWidth: 90}}>Default</th>
                <th style={{minWidth: 90}}>Active</th>
                <th style={{minWidth: 280}}>Preview</th>
                {(canUpdate || canDelete) && <th style={{minWidth: 140}}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={canUpdate || canDelete ? 7 : 6} className='text-center text-muted py-4'>
                    {searchTerm ? 'No matching records found' : 'No templates found.'}
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const canManageRow =
                    row.scopeType === 'COMPANY_GROUP'
                      ? scopeAll || Number(row.companyGroupAdminId ?? 0) === Number(actorCompanyGroupId ?? 0)
                      : false
                  return <tr key={row.id}>
                    <td className='fw-semibold text-dark'>{row.title}</td>
                    <td className='text-muted'>{String(row.scopeType || '').replace('_', ' ')}</td>
                    <td className='text-muted'>
                      {row.companyGroupAdminName || (row.scopeType === 'GLOBAL' ? 'Global' : '-')}
                    </td>
                    <td>
                      {row.defaultTemplate ? <span className='badge bg-info'>Default</span> : '-'}
                    </td>
                    <td>
                      <span className={`badge ${row.active ? 'bg-success' : 'bg-secondary'}`}>
                        {row.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className='text-muted' style={{whiteSpace: 'pre-wrap', overflowWrap: 'anywhere'}}>
                      {row.content?.slice(0, 220)}
                      {row.content && row.content.length > 220 ? '...' : ''}
                    </td>
                    {(canUpdate || canDelete) && (
                      <td>
                        <div className='d-flex gap-2'>
                          {canUpdate && canManageRow && (
                            <button className='btn btn-light-primary btn-sm' onClick={() => openEdit(row)}>
                              Edit
                            </button>
                          )}
                          {canDelete && canManageRow && (
                            <button className='btn btn-light-danger btn-sm' onClick={() => handleDelete(row)}>
                              {row.active ? 'Deactivate' : 'Delete'}
                            </button>
                          )}
                          {!canManageRow && <span className='text-muted small'>Read only</span>}
                        </div>
                      </td>
                    )}
                  </tr>
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default TermsConditionsLibraryPanel
